/**
 * Profile Personal Details Page
 * Edit name, genre, location, pincode, and other personal info
 * Uses personal details store and pincode service for address
 *
 * @author Varun Pandey
 */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { User, ArrowLeft } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { MultiSelect, MultiSelectOption } from '../../components/ui/MultiSelect';
import { Select, SelectOption } from '../../components/ui/Select';
import { Loader } from '../../components/ui/Loader';
import {
  ProfileCompletionBlocksControl,
  ProfileIncompleteCornerPing,
} from '@/components/profile/ProfileCompletionBlocksControl';

import {
  PincodeService,
  inferIndianStateFromPlace,
  type PostOffice,
} from '../../services/pincodeService';
import { usePersonalDetailsStore, type PersonalDetailsData } from '../../stores/personalDetailsStore';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import { AuthService } from '../../services/authService';

/** Cache of category subcategories (genres) by category slug so we only hit the API once per slug per session. */
const GENRE_OPTIONS_CACHE: Record<string, MultiSelectOption[]> = {};
const GENRE_OPTIONS_IN_FLIGHT: Record<string, Promise<MultiSelectOption[]> | null> = {};

const genderOptions: SelectOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

function buildLocationLabel(po: PostOffice): string {
  return `${po.Name}, ${po.District}, ${po.State}`;
}

/** Normalize for comparison (trim + lowercase). */
function norm(s: string): string {
  return String(s ?? '').trim().toLowerCase();
}

/** Find matching post office by store location (city, locality, state), or null. */
function findMatchingPostOffice(
  list: PostOffice[],
  location: { city: string; locality: string; state: string }
): PostOffice | null {
  const fullMatch = list.find(
    (po) =>
      norm(po.District) === norm(location.city) &&
      norm(po.State) === norm(location.state) &&
      norm(po.Name) === norm(location.locality)
  );
  if (fullMatch) return fullMatch;

  // Saved API data may have blank state (e.g. Delhi); match on city + locality only.
  if (!norm(location.state)) {
    return (
      list.find(
        (po) =>
          norm(po.District) === norm(location.city) &&
          norm(po.Name) === norm(location.locality)
      ) ?? null
    );
  }

  return null;
}

/** Prefer saved state; otherwise derive from pincode lookup results for the same city/locality. */
function resolveLocationState(
  location: { city: string; locality: string; state: string },
  list: PostOffice[]
): string {
  const saved = location.state.trim();
  if (saved) return saved;

  const match = findMatchingPostOffice(list, location);
  if (match?.State.trim()) return match.State.trim();

  const sameCity = list.find((po) => norm(po.District) === norm(location.city));
  if (sameCity?.State.trim()) return sameCity.State.trim();

  return inferIndianStateFromPlace(location.city, location.city);
}

function locationFromPostOffice(po: PostOffice): {
  city: string;
  pin: string;
  locality: string;
  state: string;
} {
  return {
    city: po.District,
    pin: po.Pincode,
    locality: po.Name,
    state: po.State,
  };
}

/** Map API g_slug to our Select option value. API may send "other" or "others". */
const GENDER_SLUG_TO_VALUE: Record<string, string> = {
  male: 'male',
  female: 'female',
  other: 'other',
  others: 'other',
};

/** GET returns location as array; return first item or single object for matching. */
function getFirstLocation(
  data: PersonalDetailsData | null
): { city: string; pin: string; locality: string; state: string } | null {
  const loc = data?.location;
  if (!loc) return null;
  const one = Array.isArray(loc) ? loc[0] : loc;
  return one?.city != null && one?.pin != null ? one : null;
}

/** Saved location label from data for change detection. */
function getSavedLocationLabel(data: PersonalDetailsData | null): string {
  const loc = getFirstLocation(data);
  return loc ? `${loc.locality}, ${loc.city}, ${loc.state}` : '';
}

/** Normalize API gender (g_slug) to Select option value so "Male"/"Female"/"Other" show as selected. */
function normalizeGenderFromApi(apiGender: PersonalDetailsData['gender']): string {
  if (!apiGender) return '';
  const raw =
    typeof apiGender === 'string'
      ? apiGender
      : (apiGender as { gender?: string; g_slug?: string }).g_slug ?? (apiGender as { gender?: string }).gender ?? '';
  const slug = String(raw).trim().toLowerCase();
  return GENDER_SLUG_TO_VALUE[slug] ?? (genderOptions.some((o) => o.value === slug) ? slug : '');
}

/** Sync store.data into local form state (gender, pincode, genres, location ref). */
function applyStoreDataToForm(
  data: PersonalDetailsData | null,
  setGender: (v: string) => void,
  setPincode: (v: string) => void,
  setGenres: (v: string[]) => void,
  setGenreOptions: (fn: (prev: MultiSelectOption[]) => MultiSelectOption[]) => void,
  initialApiLocationRef: React.MutableRefObject<{ city: string; pin: string; locality: string; state: string } | null>
) {
  if (!data) return;
  const genderValue = normalizeGenderFromApi(data.gender);
  if (genderValue) setGender(genderValue);
  if (data.pincode) setPincode(data.pincode);
  if (data.genre?.length) {
    setGenres(data.genre.map((g) => g.slug));
    const fromApi = data.genre.map((g) => ({ value: g.slug, label: g.name }));
    setGenreOptions((prev) => {
      const existingValues = new Set(prev.map((o) => o.value));
      const extra = fromApi.filter((o) => !existingValues.has(o.value));
      if (extra.length === 0) return prev;
      return [...prev, ...extra];
    });
  }
  const loc = getFirstLocation(data);
  if (loc) {
    initialApiLocationRef.current = loc;
    if (!data.pincode) setPincode(loc.pin);
  }
}

export const ProfilePersonalDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const data = usePersonalDetailsStore((s) => s.data);
  const isLoading = usePersonalDetailsStore((s) => s.isLoading);
  const saveLoading = usePersonalDetailsStore((s) => s.saveLoading);
  const error = usePersonalDetailsStore((s) => s.error);
  const ensureFetched = usePersonalDetailsStore((s) => s.ensureFetched);
  const updatePersonalDetails = usePersonalDetailsStore((s) => s.updatePersonalDetails);
  const setCachedLocationOptions = usePersonalDetailsStore((s) => s.setCachedLocationOptions);
  const artistDetails = useArtistDetailsStore((s) => s.data);
  const ensureArtistDetailsFetched = useArtistDetailsStore((s) => s.ensureFetched);

  const [gender, setGender] = useState<string>('');
  const [pincode, setPincode] = useState('');
  const [postOffices, setPostOffices] = useState<PostOffice[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [isLoadingPincode, setIsLoadingPincode] = useState(false);
  const [genres, setGenres] = useState<string[]>([]);
  const [genreOptions, setGenreOptions] =
    useState<MultiSelectOption[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [personalDetailsEditing, setPersonalDetailsEditing] = useState(false);
  const initialApiLocationRef = useRef<{ city: string; pin: string; locality: string; state: string } | null>(null);
  /** True only when user types in the pincode input; pincode API runs only then (when no cache). */
  const userModifiedPincodeRef = useRef(false);
  const genderRef = useRef<HTMLButtonElement>(null);
  const pincodeRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLButtonElement>(null);
  const genreSectionRef = useRef<HTMLDivElement>(null);

  const postOfficeOptions: SelectOption[] = postOffices.map((po) => {
    const label = buildLocationLabel(po);
    return { value: label, label };
  });

  // Derive gender for Select: show store value when we have API data, else user's local choice
  const genderValue = gender || (data ? normalizeGenderFromApi(data.gender) : '');

  // First time in session: fetch and update store; later visits use cached store data
  useEffect(() => {
    ensureFetched();
  }, [ensureFetched]);

  // Ensure artist details (including artist_category.slug) are fetched once per session
  useEffect(() => {
    ensureArtistDetailsFetched();
  }, [ensureArtistDetailsFetched]);

  // Fetch category subcategories (genres) once per category slug per session and merge into genre options.
  useEffect(() => {
    const slug = artistDetails?.artist_category?.slug;
    if (!slug) return;

    let cancelled = false;

    const loadGenres = async () => {
      try {
        let options: MultiSelectOption[];

        if (GENRE_OPTIONS_CACHE[slug]) {
          options = GENRE_OPTIONS_CACHE[slug];
        } else {
          if (!GENRE_OPTIONS_IN_FLIGHT[slug]) {
            GENRE_OPTIONS_IN_FLIGHT[slug] = AuthService.getSubcategories(slug)
              .then((subcats) =>
                subcats.map((s) => ({
                  value: s.slug,
                  label: s.name,
                }))
              )
              .then((mapped) => {
                GENRE_OPTIONS_CACHE[slug] = mapped;
                return mapped;
              })
              .finally(() => {
                GENRE_OPTIONS_IN_FLIGHT[slug] = null;
              });
          }
          options = await GENRE_OPTIONS_IN_FLIGHT[slug]!;
        }

        if (cancelled || !options?.length) return;

        // Merge fetched options with any existing ones (e.g. from personal-details API),
        // preferring fetched labels for overlapping slugs.
        setGenreOptions((prev) => {
          const byValue = new Map<string, MultiSelectOption>();
          options.forEach((o) => byValue.set(o.value, o));
          prev.forEach((o) => {
            if (!byValue.has(o.value)) byValue.set(o.value, o);
          });
          return Array.from(byValue.values());
        });

        // Keep only genres whose slug exists in the fetched subcategories list.
        const allowed = new Set(options.map((o) => o.value));
        setGenres((prev) => prev.filter((g) => allowed.has(g)));
      } catch (err) {
        console.error('Failed to load genres (subcategories):', err);
      }
    };

    loadGenres();

    return () => {
      cancelled = true;
    };
  }, [artistDetails?.artist_category?.slug, setGenreOptions, setGenres]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  // Sync store.data → form state whenever store updates (initial load or after PUT)
  useEffect(() => {
    if (!data) return;
    applyStoreDataToForm(
      data,
      setGender,
      setPincode,
      setGenres,
      setGenreOptions,
      initialApiLocationRef
    );
  }, [data, isLoading]);

  // When store has location and we have post office options, auto-select the matching option (keeps saved location after save)
  useEffect(() => {
    const apiLoc = getFirstLocation(data ?? null);
    if (
      !apiLoc?.city ||
      !apiLoc?.locality ||
      pincode !== apiLoc.pin
    )
      return;
    if (postOffices.length === 0) return;
    const match = findMatchingPostOffice(postOffices, apiLoc);
    if (match) {
      setSelectedLocation(buildLocationLabel(match));
    } else {
      const resolvedState = resolveLocationState(apiLoc, postOffices);
      const synthetic: PostOffice = {
        Name: apiLoc.locality,
        District: apiLoc.city,
        State: resolvedState,
        Pincode: apiLoc.pin,
        Description: null,
        BranchType: '',
        DeliveryStatus: '',
        Circle: '',
        Division: '',
        Region: '',
        Block: '',
        Country: '',
      };
      setPostOffices((prev) => [...prev, synthetic]);
      setSelectedLocation(
        `${apiLoc.locality}, ${apiLoc.city}, ${resolvedState || apiLoc.state}`
      );
    }
  }, [data, postOffices, pincode]);

  // Location options: always hit pincode API when pincode is 6 digits
  useEffect(() => {
    if (pincode.length !== 6) {
      setPostOffices([]);
      setSelectedLocation('');
      return;
    }

    const apiLoc = getFirstLocation(data ?? null) ?? initialApiLocationRef.current;

    const applyListToState = (list: PostOffice[]) => {
      setPostOffices(list);
      if (list.length === 0) {
        setSelectedLocation('');
        return;
      }
      if (
        apiLoc &&
        apiLoc.pin === pincode &&
        (apiLoc.city || apiLoc.locality || apiLoc.state)
      ) {
        const match = findMatchingPostOffice(list, apiLoc);
        if (match) {
          setSelectedLocation(buildLocationLabel(match));
        } else {
          const resolvedState = resolveLocationState(apiLoc, list);
          const synthetic: PostOffice = {
            Name: apiLoc.locality,
            District: apiLoc.city,
            State: resolvedState,
            Pincode: apiLoc.pin,
            Description: null,
            BranchType: '',
            DeliveryStatus: '',
            Circle: '',
            Division: '',
            Region: '',
            Block: '',
            Country: '',
          };
          setPostOffices((prev) => [...prev, synthetic]);
          setSelectedLocation(
            `${apiLoc.locality}, ${apiLoc.city}, ${resolvedState}`
          );
        }
        initialApiLocationRef.current = null;
      } else {
        setSelectedLocation(buildLocationLabel(list[0]));
      }
    };

    let cancelled = false;
    setIsLoadingPincode(true);
    PincodeService.getDetails(pincode)
      .then((list) => {
        if (cancelled) return;
        if (list.length > 0) {
          setCachedLocationOptions(pincode, list);
          applyListToState(list);
        } else {
          setPostOffices([]);
          setSelectedLocation('');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error in pincode effect:', err);
        setPostOffices([]);
        setSelectedLocation('');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPincode(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pincode, data, setCachedLocationOptions]);

  const handleCancelPersonalDetails = () => {
    if (!data) {
      setPersonalDetailsEditing(false);
      return;
    }
    userModifiedPincodeRef.current = false;
    applyStoreDataToForm(
      data,
      setGender,
      setPincode,
      setGenres,
      setGenreOptions,
      initialApiLocationRef
    );
    const loc = getFirstLocation(data);
    if (loc) {
      const synthetic: PostOffice = {
        Name: loc.locality,
        District: loc.city,
        State: loc.state,
        Pincode: loc.pin,
        Description: null,
        BranchType: '',
        DeliveryStatus: '',
        Circle: '',
        Division: '',
        Region: '',
        Block: '',
        Country: '',
      };
      setPostOffices([synthetic]);
      setSelectedLocation(`${loc.locality}, ${loc.city}, ${loc.state}`);
    } else {
      setPostOffices([]);
      setSelectedLocation('');
    }
    setErrors({});
    setPersonalDetailsEditing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!genderValue) newErrors.gender = 'Please select your gender';
    if (!pincode || pincode.length !== 6)
      newErrors.pincode = 'Enter a valid 6-digit pincode';
    if (genres.length === 0) newErrors.genres = 'Select at least one genre';

    if (postOfficeOptions.length > 0 && !selectedLocation) {
      newErrors.location = 'Please select a location';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      const message = newErrors[firstKey];
      toast.error(message);
      if (firstKey === 'gender') genderRef.current?.focus();
      else if (firstKey === 'pincode') pincodeRef.current?.focus();
      else if (firstKey === 'location') locationRef.current?.focus();
      else if (firstKey === 'genres') genreSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    let locationPayload: Array<{
      city: string;
      pin: string;
      locality: string;
      state: string;
    }> = [];
    if (postOffices.length > 0 && selectedLocation) {
      const po = postOffices.find(
        (p) => buildLocationLabel(p) === selectedLocation
      );
      if (po) {
        const loc = locationFromPostOffice(po);
        loc.state =
          loc.state.trim() ||
          resolveLocationState(
            { city: loc.city, locality: loc.locality, state: loc.state },
            postOffices
          ) ||
          inferIndianStateFromPlace(loc.city, loc.city);
        locationPayload = [loc];
      }
    }

    if (locationPayload.length > 0 && !locationPayload[0].state.trim()) {
      const msg = 'Could not determine state for this location. Please re-select your location.';
      setErrors((prev) => ({ ...prev, location: msg }));
      toast.error(msg);
      locationRef.current?.focus();
      return;
    }

    if (
      locationPayload.length === 0 &&
      (pincode.length !== 6 || postOffices.length === 0)
    ) {
      const msg = 'Please enter pincode and select location';
      setErrors((prev) => ({ ...prev, location: msg }));
      toast.error(msg);
      locationRef.current?.focus();
      return;
    }

    try {
      const genderPayload = genderValue.toLowerCase() === 'other' ? 'others' : genderValue.toLowerCase();
      await updatePersonalDetails({
        gender: genderPayload,
        pincode,
        genre: genres,
        location: locationPayload,
      });
      if (postOffices.length > 0) setCachedLocationOptions(pincode, postOffices);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.submit;
        return next;
      });
      setPersonalDetailsEditing(false);
    } catch {
      toast.error('Failed to save. Please try again.');
    }
  };

  const savedGender = data ? normalizeGenderFromApi(data.gender) : '';
  const savedPincode = data?.pincode ?? '';
  const savedGenresKey = (data?.genre ?? []).map((g) => g.slug).sort().join(',');
  const currentGenresKey = [...genres].sort().join(',');
  const savedLocationLabel = getSavedLocationLabel(data);
  const hasPersonalDetailsChanged = useMemo(() => {
    if (!data) return false;
    if (genderValue !== savedGender) return true;
    if (pincode !== savedPincode) return true;
    if (currentGenresKey !== savedGenresKey) return true;
    if (postOfficeOptions.length > 0 && selectedLocation !== savedLocationLabel) return true;
    return false;
  }, [data, genderValue, savedGender, pincode, savedPincode, currentGenresKey, savedGenresKey, postOfficeOptions.length, selectedLocation, savedLocationLabel]);

  const personalDetailsNeedAttention = useMemo(() => {
    const raw = artistDetails?.incompleted_sections;
    if (!Array.isArray(raw)) return false;
    const set = new Set(raw);
    return set.has('gender') || set.has('location');
  }, [artistDetails?.incompleted_sections]);

  return (
    <div className="w-full bg-[#0B0B0D] text-white">
      <div className="w-full mx-auto md:px-4 md:py-6 lg:py-8 pb-16">
        <header className="mb-6 lg:mb-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex min-h-[44px] shrink-0 items-center gap-2 -ml-2 rounded-xl pl-2 pr-3 text-[#8E8E93] transition-colors hover:bg-[#1C1C1E] hover:text-white active:bg-[#2C2C2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <ProfileCompletionBlocksControl />
          </div>
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
                <User className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                  Personal Details
                </h1>
                <p className="text-[#8E8E93] text-sm lg:text-base mt-0.5">
                  Help us understand who you are and what you perform
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 justify-end gap-2 sm:w-auto w-full">
              {!personalDetailsEditing ? (
                <span className="relative inline-flex shrink-0">
                  <button
                    type="button"
                    onClick={() => setPersonalDetailsEditing(true)}
                    className="relative z-0 inline-flex items-center gap-1.5 rounded-full border border-[#3A3A3C] bg-[#151517] px-3 py-1.5 text-[11px] text-[#F2F2F7] text-nowrap transition-colors hover:bg-[#2C2C2E]"
                  >
                    Modify
                  </button>
                  {personalDetailsNeedAttention ? (
                    <ProfileIncompleteCornerPing className="right-0 top-0 z-[2] md:right-0 md:top-0" />
                  ) : null}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleCancelPersonalDetails}
                    className="inline-flex items-center rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="personal-details-form"
                    disabled={!hasPersonalDetailsChanged || saveLoading || isLoading}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white text-black border border-[#3A3A3C] px-4 py-2 text-xs font-medium hover:bg-white/90 hover:text-black transition-colors text-nowrap disabled:opacity-50 disabled:pointer-events-none disabled:bg-[#3A3A3C] disabled:text-[#8E8E93]"
                  >
                    {saveLoading ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </div>
        </header>

        <motion.form
          id="personal-details-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
          className="space-y-6 lg:space-y-8"
        >
          {isLoading ? (
            <section className="rounded-2xl p-5 lg:p-6 flex items-center justify-center min-h-[200px]">
              <Loader size="small" />
            </section>
          ) : !personalDetailsEditing ? (
            /* View mode: same layout as edit form, read-only, no borders */
            <section className="rounded-2xl">
              <div className="divide-y divide-[#3A3A3C]/30">
                <div className="flex flex-col gap-1 py-4 first:pt-0 md:flex-row md:items-baseline md:gap-4 md:py-5">
                  <span className=" shrink-0 text-xs font-medium uppercase tracking-wider text-[#8E8E93] md:w-28">
                    Gender
                  </span>
                  <span className="text-sm font-medium text-[#E5E5EA]">
                    {savedGender ? (genderOptions.find((g) => g.value === savedGender)?.label ?? savedGender) : '—'}
                  </span>
                </div>
                <div className="flex flex-col gap-1 py-4 md:flex-row md:items-baseline md:gap-4 md:py-5">
                  <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-[#8E8E93] md:w-28">
                    Pincode
                  </span>
                  <span className="text-sm font-medium tabular-nums text-[#E5E5EA]">
                    {savedPincode || '—'}
                  </span>
                </div>
                {savedLocationLabel ? (
                  <div className="flex flex-col gap-1 py-4 md:flex-row md:items-baseline md:gap-4 md:py-5">
                    <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-[#8E8E93] md:w-28">
                      Location
                    </span>
                    <span className="text-sm font-medium text-[#E5E5EA] truncate">
                      {savedLocationLabel}
                    </span>
                  </div>
                ) : null}
                <div className="flex flex-col gap-1 py-4 last:pb-0 md:flex-row md:items-baseline md:gap-4 md:py-5">
                  <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-[#8E8E93] md:w-28">
                    Genre
                  </span>
                  <span className="text-sm font-medium text-[#E5E5EA]">
                    {data?.genre?.length
                      ? data.genre.map((g) => g.name).join(', ')
                      : '—'}
                  </span>
                </div>
              </div>
            </section>
          ) : (
            /* Edit mode: form inputs */
            <section className="rounded-2xl space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Select
                  ref={genderRef}
                  label="Gender"
                  options={genderOptions}
                  value={genderValue}
                  onChange={setGender}
                  placeholder="Select your gender"
                  error={errors.gender}
                  hideErrorText
                />

                <Input
                  ref={pincodeRef}
                  label="Pincode"
                  value={pincode}
                  onChange={(e) => {
                    userModifiedPincodeRef.current = true;
                    const numeric = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setPincode(numeric);
                    if (errors.pincode) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.pincode;
                        return next;
                      });
                    }
                  }}
                  placeholder="Enter 6-digit pincode"
                  inputMode="numeric"
                  error={errors.pincode}
                  hideErrorText
                  isLoading={isLoadingPincode}
                />
              </div>

              {postOfficeOptions.length > 0 && (
                <Select
                  ref={locationRef}
                  label="Location"
                  options={postOfficeOptions}
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  placeholder="Select location"
                  error={errors.location}
                  hideErrorText
                />
              )}

              <div ref={genreSectionRef}>
                <MultiSelect
                  label="Genre"
                  options={genreOptions}
                  value={genres}
                  onChange={setGenres}
                  error={errors.genres}
                  hideErrorText
                />
              </div>
            </section>
          )}


        </motion.form>
      </div>
    </div>
  );
};
